import { useEffect, useMemo, useState } from 'react'
import MultiLocationSelector from 'app/components/MultiLocationSelector'
import type { LocationJson, LocationType } from 'app/types/entities'
import type { UserJson } from 'app/types/entities'
import { SearchResponse } from 'app/services/search'
import { useUser } from 'app/utils'
import { bg } from 'lib/background'
import { debug } from 'lib/logging'
import { gqlFetchList } from 'app/lib/graphqlClient'

type LocationUser = Partial<Pick<UserJson, 'role' | 'allowedLocationIds' | 'assignedLocationIds' | 'workspaces'>>

type LocationParams = {
  isNewUser?: boolean
  user: LocationUser
  currentWorkspaceId?: string
}

/** Resolve the location IDs stored for the current workspace. */
const resolveLocationIds = (user: LocationUser, currentWorkspaceId?: string): string[] | undefined => {
  // workspaces[] is the source of truth per workspace; no fallback to
  // allowedLocationIds, which would leak another workspace's locations.
  if (user.workspaces?.length) {
    return user.workspaces.find((w) => w.id === currentWorkspaceId)?.locationIds
  }
  return user.allowedLocationIds ?? undefined
}

/** Determine whether the location selector should show "All Locations" on initial render */
export const getInitialAllLocations = ({ isNewUser, user, currentWorkspaceId }: LocationParams): boolean => {
  if (isNewUser) return true
  return !resolveLocationIds(user, currentWorkspaceId)?.length
}

/** Get the location IDs to load for the current workspace context */
export const getLocationIdsToLoad = ({ user, currentWorkspaceId }: LocationParams): string[] | undefined => {
  const ids = resolveLocationIds(user, currentWorkspaceId)
  return ids?.length ? ids : undefined
}

/** Scope dropdown options to the locations the viewer can grant (empty = all). */
export const getLocationOptionsFilter = ({
  viewerAllowedLocationIds,
}: {
  viewerAllowedLocationIds: string[] | undefined
}): { id: string } | undefined =>
  viewerAllowedLocationIds?.length
    ? { id: `(${viewerAllowedLocationIds.map((id) => `"${id}"`).join(` OR `)})` }
    : undefined

type Props = {
  user: UserJson
  isNewUser: boolean
  isOrgLevel: boolean
  setIsDirtyForm: (dirty: boolean) => void
  onHiddenInputsChange?: (inputs: Array<{ name: string; value: string }>) => void
  disabled?: boolean
}

export default function UserLocationSelector({
  user,
  isNewUser,
  isOrgLevel,
  setIsDirtyForm,
  onHiddenInputsChange,
  disabled,
}: Props) {
  const me = useUser()

  const [allowedLocations, setAllowedLocations] = useState<LocationType[]>([])
  const [allLocations, setAllLocations] = useState(() =>
    getInitialAllLocations({
      isNewUser,
      user,
      currentWorkspaceId: me?.workspaceId,
    }),
  )

  async function fetchLocations(locationIds: string[]): Promise<SearchResponse<LocationJson>> {
    const result = await gqlFetchList<LocationJson>(
      'locations',
      { search: `id:(${locationIds.map((id) => `"${id}"`).join(` OR `)})` },
      'id name orgId workspaceId',
    )
    return { hits: result.hits as any, total: result.total, page: result.page }
  }

  const allLocationsNow = getInitialAllLocations({
    isNewUser,
    user,
    currentWorkspaceId: me?.workspaceId,
  })
  const resolvedLocationIds = getLocationIdsToLoad({
    user,
    currentWorkspaceId: me?.workspaceId,
  })
  const resolvedLocationIdsKey = resolvedLocationIds?.join(`,`) ?? ``

  useEffect(() => {
    if (!me) return
    const loadLocations = async () => {
      if (!allLocationsNow && resolvedLocationIds?.length) {
        debug(`location`, `loading locationIds`, resolvedLocationIds)
        const response = await fetchLocations(resolvedLocationIds)
        if (response?.hits) {
          setAllLocations(false)
          setAllowedLocations(response.hits as LocationType[])
          return
        }
      }
      setAllLocations(allLocationsNow)
      if (allLocationsNow) setAllowedLocations([])
    }
    bg(loadLocations())
  }, [me?.workspaceId, allLocationsNow, resolvedLocationIdsKey])

  useEffect(() => {
    if (onHiddenInputsChange) {
      const hiddenInputs = allowedLocations.map((l) => ({ name: `allowedLocationIds`, value: l?.id || `` }))
      onHiddenInputsChange(hiddenInputs)
    }
  }, [allowedLocations])

  // Memoize to prevent infinite re-renders in ComboMultiSelectAsync's additionalParams effect
  const filterLocations = useMemo(
    () => getLocationOptionsFilter({ viewerAllowedLocationIds: me?.allowedLocationIds }),
    [me?.allowedLocationIds],
  )

  if (!me) return null

  const handleLocationChangeV2 = async (locationIds: string[]) => {
    debug(`location`, `handleLocationChange`, locationIds)
    setIsDirtyForm(true)

    if (locationIds.length === 0) {
      setAllLocations(true)
      setAllowedLocations([])
    } else {
      setAllLocations(false)
      const response = await fetchLocations(locationIds)
      if (response?.hits) {
        setAllowedLocations(response.hits as LocationType[])
      }
    }
  }

  const commonPropsV2 = {
    allLocations,
    allowedLocations,
    isOrgLevel,
    getFilterLocations: filterLocations,
  }

  return <MultiLocationSelector {...commonPropsV2} onLocationChange={handleLocationChangeV2} disabled={disabled} />
}
